import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import {
  SplitDefinition, // Import type
  StratifyDocument,
} from "@stratify/shared";
import React, { useEffect, useState } from "react"; // Import useEffect
import { useDispatch, useSelector } from "react-redux"; // Import Redux hooks
import { RootState } from "../../../store/store"; // Import RootState type
import { fetchSuggestionsStart } from "../store/documentsSlice"; // Import action

interface SplitDocumentModalProps {
  open: boolean;
  onClose: () => void;
  document: StratifyDocument;
  onSubmit: (splits: SplitDefinition[]) => Promise<void>;
}

interface SplitFormEntry {
  id: number; // For unique key prop
  name: string;
  startPage: string; // Use string for TextField
  endPage: string; // Use string for TextField
}

export const SplitDocumentModal: React.FC<SplitDocumentModalProps> = ({
  open,
  onClose,
  document,
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const {
    data: suggestions,
    loading: suggestionsLoading,
    error: suggestionsError,
  } = useSelector((state: RootState) => {
    // Provide a default structure if the entry doesn't exist
    return (
      state.documents.suggestionsByDocId[document.id] || {
        data: null,
        loading: false,
        error: null,
      }
    );
  });

  // Local state for the form entries
  const [splits, setSplits] = useState<SplitFormEntry[]>([
    { id: 1, name: "", startPage: "", endPage: "" },
  ]);
  const [nextId, setNextId] = useState(2);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null); // Use localError for form validation

  const totalPages = document.metadata?.processing?.result?.pageCount ?? 0;

  // Fetch suggestions when the modal opens
  useEffect(() => {
    if (open && document.id) {
      // Reset local state first in case suggestions are fetched again
      setSplits([{ id: 1, name: "", startPage: "", endPage: "" }]);
      setNextId(2);
      setLocalError(null);
      dispatch(fetchSuggestionsStart(document.id));
    }
  }, [open, document.id, dispatch]);

  // Pre-populate form when suggestions are loaded
  useEffect(() => {
    if (open && suggestions && suggestions.length > 0) {
      // Sort suggestions by startPage before mapping
      const sortedSuggestions = [...suggestions].sort(
        (a, b) => a.startPage - b.startPage
      );
      const initialSplits = sortedSuggestions.map((s, index) => ({
        id: index + 1, // Generate sequential IDs for the form
        name: s.documentName,
        startPage: s.startPage.toString(),
        endPage: s.endPage.toString(),
      }));
      setSplits(initialSplits);
      setNextId(suggestions.length + 1);
    } else if (open && suggestions === null && !suggestionsLoading) {
      // If suggestions were fetched but none found, ensure default state
      setSplits([{ id: 1, name: "", startPage: "", endPage: "" }]);
      setNextId(2);
    }
    // Only run when suggestions data changes after opening
  }, [suggestions, open, suggestionsLoading]); // Depend on suggestions data

  const handleAddSplit = () => {
    setSplits([
      ...splits,
      { id: nextId, name: "", startPage: "", endPage: "" },
    ]);
    setNextId(nextId + 1);
  };

  const handleRemoveSplit = (idToRemove: number) => {
    if (splits.length > 1) {
      setSplits(splits.filter((split) => split.id !== idToRemove));
    }
  };

  // Removed the duplicate handleRemoveSplit declaration that was here.

  const handleSplitChange = (
    id: number,
    field: keyof Omit<SplitFormEntry, "id">,
    value: string
  ) => {
    setSplits(
      splits.map((split) =>
        split.id === id ? { ...split, [field]: value } : split
      )
    );
  };

  const validateSplits = (): SplitDefinition[] | null => {
    const validatedSplits: SplitDefinition[] = [];
    for (const split of splits) {
      const name = split.name.trim();
      const startPage = parseInt(split.startPage, 10);
      const endPage = parseInt(split.endPage, 10);

      if (!name) {
        setLocalError("All splits must have a name.");
        return null;
      }
      if (isNaN(startPage) || startPage < 1) {
        setLocalError(
          `Invalid start page '${split.startPage}' for split '${name}'. Must be a number >= 1.`
        );
        return null;
      }
      if (isNaN(endPage) || endPage < startPage) {
        setLocalError(
          `Invalid end page '${split.endPage}' for split '${name}'. Must be a number >= start page.`
        );
        return null;
      }
      if (totalPages > 0 && (startPage > totalPages || endPage > totalPages)) {
        setLocalError(
          `Page numbers for split '${name}' exceed the document's total pages (${totalPages}).`
        );
        return null;
      }
      validatedSplits.push({ name, startPage, endPage });
    }
    setLocalError(null); // Clear local validation error
    return validatedSplits;
  };

  const handleSubmit = async () => {
    const validated = validateSplits();
    if (!validated) {
      return;
    }

    setIsSubmitting(true);
    setLocalError(null); // Clear local error before submitting
    try {
      await onSubmit(validated);
      handleClose(); // Close on success
    } catch (err: any) {
      setLocalError(err.message || "Failed to initiate document split."); // Use localError for submission errors too
      console.error("Split submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset local state explicitly on close
    setSplits([{ id: 1, name: "", startPage: "", endPage: "" }]);
    setNextId(2);
    setLocalError(null);
    setIsSubmitting(false);
    // Note: We don't reset the Redux suggestions state here,
    // it will be refetched if the modal is reopened.
    onClose();
  };

  // Combine local validation error and suggestion fetch error
  const displayError = localError || suggestionsError;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {" "}
      {/* Changed maxWidth to "md" */}
      <DialogTitle>Split Document: {document.filename}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Define the sections of the document you want to split into separate
          child documents. Each section requires a name and the start/end page
          numbers (inclusive). The original document will remain unchanged.
          {totalPages > 0 && ` This document has ${totalPages} pages.`}
        </DialogContentText>

        {displayError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {displayError}
          </Alert>
        )}

        {suggestionsLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <CircularProgress size={24} /> Loading suggestions...
          </Box>
        )}

        {!suggestionsLoading &&
          splits.map((split) => (
            <Box
              key={split.id}
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
            >
              <TextField
                label="Child Document Name"
                value={split.name}
                onChange={(e) =>
                  handleSplitChange(split.id, "name", e.target.value)
                }
                sx={{
                  flex: 1,
                }}
                required
                size="small"
                disabled={isSubmitting || suggestionsLoading}
              />
              <TextField
                label="Start Page"
                type="number"
                value={split.startPage}
                onChange={(e) =>
                  handleSplitChange(split.id, "startPage", e.target.value)
                }
                required
                size="small"
                inputProps={{
                  min: 1,
                  max: totalPages > 0 ? totalPages : undefined,
                }}
                disabled={isSubmitting || suggestionsLoading}
              />
              <TextField
                label="End Page"
                type="number"
                value={split.endPage}
                onChange={(e) =>
                  handleSplitChange(split.id, "endPage", e.target.value)
                }
                required
                size="small"
                inputProps={{
                  min: split.startPage || 1,
                  max: totalPages > 0 ? totalPages : undefined,
                }}
                disabled={isSubmitting || suggestionsLoading}
              />
              <IconButton
                onClick={() => handleRemoveSplit(split.id)}
                disabled={
                  splits.length <= 1 || isSubmitting || suggestionsLoading
                }
                size="small"
                color="error"
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
            </Box>
          ))}
        {!suggestionsLoading && (
          <Button
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleAddSplit}
            disabled={isSubmitting || suggestionsLoading}
          >
            Add Another Split
          </Button>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isSubmitting || suggestionsLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || suggestionsLoading || splits.length === 0}
        >
          {isSubmitting ? "Splitting..." : "Start Split"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
